/**
 * File: src/pages/Dashboard/campaigns/campaign-create/index.js
 * Description: Campaign creation workflow page handling steps, forms, preview, validation, and submission.
 * Dependencies: React, Ant Design, Redux, React Router, i18n, custom components, and network utilities.
 * Guidelines:
 *  - Use function-level JSDoc for non-trivial helpers and components.
 *  - Keep UI strings translatable via i18n where applicable.
 *  - Avoid deep prop drilling; prefer Redux/selectors where state is shared.
 * Created: 2025-09-23
 * Last Updated: 2025-09-23
 */

// React core imports
import React, { Component, useEffect, useState } from 'react'

// Network and API utilities
import network from 'network/network';

// Ant Design components for UI elements
import { Form, notification, Tooltip, Collapse, Button, message, Steps, theme, Select, Row, Col, Tag, Table, Checkbox, Card, Input, Space, Descriptions, Badge, Spin, Modal, Typography, List, Radio, Alert } from 'antd';

// Ant Design icons for visual elements
import { SendOutlined, CloseOutlined, CheckOutlined, EditOutlined, UnorderedListOutlined, AlignCenterOutlined, EyeOutlined, CopyOutlined, FontSizeOutlined, CheckCircleFilled, UsergroupAddOutlined, CheckCircleOutlined, ExclamationCircleOutlined, SearchOutlined, RollbackOutlined, ArrowRightOutlined, UserOutlined, PlusCircleOutlined, FileTextOutlined, SaveOutlined, LoadingOutlined, FormOutlined, BugOutlined, SyncOutlined } from '@ant-design/icons';

// Custom CSS for step components
import './Steps.css';

// React Router for navigation
import { useNavigate } from 'react-router-dom';

// Internationalization support
import { useTranslation } from 'react-i18next';

// Custom components
import ReusableButton from 'components/Elements/ReusableButton';
import Flex from 'components/shared-components/Flex';

// Constants for messaging limits
import { LIMIT_TIER_100K, LIMIT_TIER_10K, LIMIT_TIER_1K, LIMIT_TIER_2K } from 'constants/LimitConstant';

// Redux state management
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getDailyUsage } from 'utils/redux/reducers/dailyUsageReducer';
// Destructure Typography components
const { Text, Link } = Typography;

// Destructure Ant Design components
const { Panel } = Collapse;
const { Option } = Select;

/**
 * Custom tag renderer for Select component
 * Renders tags with custom styling and behavior
 * 
 * @param {Object} props - Tag properties
 * @param {string} props.label - Display label for the tag
 * @param {any} props.value - Value of the tag
 * @param {boolean} props.closable - Whether tag can be closed
 * @param {Function} props.onClose - Handler for tag close event
 * @returns {JSX.Element} Styled tag component
 */
const tagRender = (props) => {
    const { label, value, closable, onClose } = props;

    // Prevent default mouse down behavior to avoid unwanted selection
    const onPreventMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <Tag
            color={'blue'}
            onMouseDown={onPreventMouseDown}
            closable={closable}
            onClose={onClose}
            style={{
                marginRight: 3,
            }}
        >
            {label}
        </Tag>
    );
};

/**
 * Enum for different customer group types used in campaign targeting
 * Each type represents a different way to select campaign recipients
 */
const customerGroupTypes = {
    customerGroups: 1,      // Regular customer groups
    eventCustomers: 2,      // Customers from events
    standCustomers: 3,      // Customers from stands/booths
    sessionCustomers: 4,    // Active session customers
    customerSelect: 5,      // Manually selected customers
    coffeeCustomers: 6,     // Coffee shop customers
    splitedCustomers: 7     // Split/segmented customers
}

/**
 * CreateCampaigns Component
 * 
 * A comprehensive campaign creation interface that allows users to:
 * - Select message templates
 * - Choose target customer groups
 * - Configure campaign parameters
 * - Preview messages before sending
 * - Send test messages
 * - Track usage limits and credits
 * 
 * @param {Object} props - Component properties
 * @returns {JSX.Element} Campaign creation interface
 */
const CreateCampaigns = (props) => {
    // Redux dispatch for state management
    const dispatch = useDispatch();

    // Get daily usage data from Redux store for limit tracking
    const dailyUsage = useSelector(store => store.dailyUsage.data);



    // Loading icon for spinner components
    const Icon = <LoadingOutlined style={{ fontSize: 35 }} spin />

    // Translation hook for multi-language support
    const { t, i18n } = useTranslation();

    // UI State Variables
    const [buttonVariable, setButtonVariable] = useState('')  // Dynamic button variable storage

    // Data Lists for Campaign Configuration
    const [fromData, setFromData] = useState([])              // Available sender numbers/accounts
    const [toData, setToData] = useState(new Array())         // Available customer groups
    const [branchList, setBranchList] = useState(new Array()) // Available business branches

    // Customer Lists for Different Target Types
    const [activeSessionClients, setActiveSessionClients] = useState([])     // Currently active customers
    const [activeSessionClientIds, setActiveSessionClientIds] = useState([]) // IDs of active customers
    const [eventCustomerList, setEventCustomerList] = useState([])           // Customers from events
    const [standCustomerList, setStandCustomerList] = useState([])           // Customers from stands
    const [customerList, setCustomerList] = useState([])                     // General customer list
    const [splitedCustomerList, setSplitedCustomerList] = useState([])       // Segmented customers

    // Template and Message Configuration
    const [parameterData, setParameterData] = useState([])     // Template parameters
    const [templatesData, setTemplatesData] = useState([])     // Available message templates
    const [variable, setVariable] = useState(new Array())      // Template variables

    // Input Controls
    const [input, setInput] = useState(true)                   // Input field visibility
    const [inputValue, setInputValue] = useState("")           // Current input value
    const [inputEnable, setInputEnable] = useState(true)       // Input field enabled state

    // Test Message Configuration
    const [testPhone, setTestPhone] = useState()               // Phone number for test messages
    const [rawPhone, setRawPhone] = useState()                 // Raw phone number input

    // Main campaign configuration state object
    // Contains all necessary data for creating and sending a campaign
    const [campaignState, setCampaignState] = useState({
        // Basic Campaign Information
        campaignName: "",                           // Name/title of the campaign
        campaignFrom: "",                           // Sender number/account ID

        // Target Recipients Configuration
        campaignTo: [],                             // Array of target group IDs
        campaignToName: [],                         // Array of target group names
        campaignToCustomers: [],                    // Array of selected individual customers
        campaignToSplitedCustomers: [],             // Array of segmented customers

        // Template Configuration
        chooseTemplateId: "",                       // Selected template ID
        chooseTemplateName: "",                     // Selected template name
        chooseTemplateMessage: "",                  // Template message content
        chooseTemplateType: "",                     // Template type (text, media, etc.)
        chooseTemplateDynamicCount: "",             // Number of dynamic variables
        chooseTemplateDynamicButtonCount: "",       // Number of dynamic buttons
        chooseTemplateButtonsData: [],              // Button configuration data
        chooseTemplateContent: "",                  // Full template content
        mediaId: "",                                // Media attachment ID

        // Specialized Customer Groups
        campaignEventCustomers: [],                 // Customers from events
        campaignEventCustomersName: [],             // Names of event customer groups
        campaignStandCustomers: [],                 // Customers from stands/booths
        campaignStandCustomersName: [],             // Names of stand customer groups
        campaignBranchs: [],                        // Selected business branches
    });
    // Search and Filter State
    const [searchValue, setSearchValue] = useState('')                        // Template search input
    const [filteredTemplates, setFilteredTemplates] = useState(templatesData) // Filtered template results

    // UI Styling Objects
    const panelErrorStyle = {
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: "red ",
        borderRadius: "5px",
        marginTop: "1px",
    }
    const panelInfoStyle = {
        // Default panel styling (currently empty)
    }

    // Form Validation States
    const [fromValidation, setFromValidation] = useState(false)               // Sender validation error
    const [toValidation, setToValidation] = useState(false)                   // Recipient validation error
    const [chooseTemplateValidation, setChooseTemplateValidation] = useState(false) // Template validation error
    const [contentValidation, setContentValidation] = useState(false)         // Content validation error
    const [campaignNameValidation, setCampaignNameValidation] = useState(false) // Campaign name validation error

    // UI Control States
    const [api, contextHolder] = notification.useNotification()               // Ant Design notification API
    const [loadingPanel, setLoadingPanel] = useState(false)                   // Global loading state
    const [modalVisible, setModalVisible] = useState(false)                   // Confirmation modal visibility

    // Credit and Limit Management
    const [totalCredit, setTotalCredit] = useState(0)                         // Available message credits
    const [limits, setLimits] = useState({})                                  // Daily/monthly limits
    const [isLimitLoading, setIsLimitLoading] = useState(true)                // Limit data loading state

    // Customer Group Selection
    const [selectedCustomerGroupType, setSelectedCustomerGroupType] = useState(customerGroupTypes.customerGroups) // Current group type

    // Navigation hook for programmatic routing
    const navigate = useNavigate();

    /**
     * Initial component setup effect
     * Loads essential data when component mounts:
     * - Daily usage statistics for limit tracking
     * - Available message templates
     */
    useEffect(() => {
        dispatch(getDailyUsage());
        getAllTemplate();
    }, []);

    /**
     * Daily usage data processing effect
     * Triggered when daily usage data is loaded from Redux store
     * Fetches sender data and credit information
     */
    useEffect(() => {
        if (dailyUsage?.data) {
            responseFromData();      // Load available sender numbers
            selectTotalCredit();     // Load credit balance
        }
    }, [dailyUsage]);

    /**
     * Customer group type change effect
     * Resets all customer selections when switching between group types
     * Prevents invalid selections when changing targeting methods
     */
    useEffect(() => {
        setCampaignState({
            ...campaignState,
            // Reset all customer group selections
            campaignEventCustomers: [],
            campaignEventCustomersName: [],
            campaignStandCustomers: [],
            campaignStandCustomersName: [],
            campaignTo: [],
            campaignToName: [],
        });
    }, [selectedCustomerGroupType]);

    /**
     * Fetches the current total credit balance for WhatsApp messages
     * Credits are consumed when sending campaign messages
     * 
     * @async
     * @function selectTotalCredit
     */
    const selectTotalCredit = async () => {
        const result = await network.get("/admin/campaign/total-credit");
        if (result.isSuccess) {
            setTotalCredit(result.data.creditCount);
        }
    }

    /**
     * Determines if the current application context is an event management app
     * Used to show/hide event-specific features and customer groups
     * 
     * @returns {boolean} True if current app is event management
     */
    const isEventApp = () => {
        const selectedApp = JSON.parse(localStorage.getItem("selectedApp") || "{}")
        if (selectedApp && selectedApp.appName == "event") {
            return true;
        }
        return false;
    }

    /**
     * Determines if the current application context is a coffee shop app
     * Used to show/hide coffee-specific features and customer groups
     * 
     * @returns {boolean} True if current app is coffee management
     */
    const isCoffeeApp = () => {
        const selectedApp = JSON.parse(localStorage.getItem("selectedApp") || "{}")
        if (selectedApp && selectedApp.appName == "coffeeproject") {
            return true;
        }
        return false;
    }

    // Cache app type checks for performance
    const isCoffee = isCoffeeApp();
    const isEvent = isEventApp();

    /**
     * Processes and edits dynamic button content for message templates
     * Handles variable substitution in button text using {{variable}} syntax
     * Supports dynamic content replacement for personalized buttons
     */
    const changeButtonEdit = () => {
        // Process buttons only if there are no dynamic button count restrictions
        if (campaignState?.chooseTemplateDynamicButtonCount == 0) {
            // Regular expression to find variable placeholders like {{1}}, {{2}}, etc.
            let regex = /{{(\d+)}}/g;
            let text = campaignState.chooseTemplateButtonsData[0]
            let matches = String(text).match(regex);
            let button = [];
            let srtButton = "";
            let bracketop = "{{"  // Opening bracket for variables
            let bracketcl = "}}"  // Closing bracket for variables

            // Process each variable placeholder found in the button text
            if (matches) {
                let currentIndex = 0;
                matches.forEach(matchValue => {

                    const number = parseInt(matchValue.match(/\d+/)[0], 10);

                    const matchIndex = text.indexOf(matchValue);

                    const outsideText = text.slice(currentIndex, matchIndex);

                    button.push((<span >{outsideText}</span>));
                    srtButton += outsideText;


                    // component.push((<span className=" text-danger  font-size-md" >{buttonVariable}</span>));

                    button.push((<span className=" text-danger  font-size-md">{bracketop}test{bracketcl}</span>));
                    srtButton += bracketop + buttonVariable + bracketcl
                    currentIndex = matchIndex + matchValue.length;

                });

                // Handle remaining text after the last match
                if (currentIndex < text.length) {
                    const remainingText = text.slice(currentIndex);
                    if (remainingText) {
                        button.push((<span>{remainingText}</span>));
                        srtButton += remainingText
                    }
                }
            }
            else {
                if (text) {
                    // If no matches found, treat entire text as static content
                    button.push((<span>{text}</span>));
                    srtButton += text
                }
            }
            return srtButton;
        }
    }

    /**
     * Renders button content with dynamic input fields for variable substitution
     * Creates interactive inputs for users to fill in template variables
     * Used when template has dynamic button parameters that need user input
     * 
     * @returns {JSX.Element|undefined} Button content with input fields or undefined
     */
    const contentButton = () => {
        // Only render if template has dynamic button parameters
        if (campaignState?.chooseTemplateDynamicButtonCount != 0) {
            // Regular expression to find variable placeholders
            let regex = /{{(\d+)}}/g;
            let text = campaignState.chooseTemplateButtonsData[0]
            let matches = String(text).match(regex);
            let component = [];

            if (matches) {
                let currentIndex = 0;
                matches.forEach(matchValue => {
                    const number = parseInt(matchValue.match(/\d+/)[0], 10);
                    const matchIndex = text.indexOf(matchValue);

                    const outsideText = text.slice(currentIndex, matchIndex);
                    component.push((<span >{outsideText}</span>));



                    // component.push((<span className=" text-danger  font-size-md" >{buttonVariable}</span>));


                    component.push((<span className=" text-danger  font-size-md"><Input onChange={inputChange} style={{
                        width: "200px",
                        height: "30px",
                        marginTop: "10px",
                        marginBottom: "15px",
                        marginLeft: "5px",
                        marginRight: "5px",

                    }}></Input></span>));

                    currentIndex = matchIndex + matchValue.length;

                });

                // Eğer metin, son eşleşme sonrasında devam ediyorsa, kalan kısmı da ekrana yazdır
                if (currentIndex < text.length) {
                    const remainingText = text.slice(currentIndex);

                    component.push((<span>{remainingText}</span>));
                }
            }
            else {
                // Eğer eşleşme bulunamadıysa, metnin tamamını dışındaki metin olarak kabul edebilirsiniz.

                component.push((<span>{text}</span>));
            }
            return (
                <div >
                    <Col className='d-flex align-items-center justify-content-center'> <b>Buton İçeriği</b>
                    </Col>
                    <div className=" ml-4 p-4" style={{ background: "#e8e8e8", borderRadius: 10 }}>
                        {component}
                    </div>
                </div>
            )
        }

    }
    /**
     * Utility function to remove square brackets from text
     * Used to clean up button text that comes with bracket formatting
     * 
     * @param {string} text - Text containing square brackets
     * @returns {string} Text with brackets removed
     */
    const removeBrackets = (text) => {
        return text.replace(/[\[\]]/g, "");
    };
    /**
     * Renders phone/action buttons for message templates
     * Handles both dynamic buttons (with variables) and static buttons
     * Supports different button types: URL buttons, call-to-action buttons
     * 
     * @returns {JSX.Element|JSX.Element[]} Button components for message preview
     */
    /**
     * Renders template buttons with optional dynamic variables.
     * When the template includes placeholders like {{1}}, they are replaced
     * by runtime values and previewed accordingly.
     *
     * @returns {JSX.Element|JSX.Element[]} Rendered button(s) for preview
     */
    const phoneButton = () => {
        // Handle dynamic buttons with variable substitution
        if (campaignState?.chooseTemplateDynamicButtonCount != 0) {
            let regex = /{{(\d+)}}/g;
            let text = campaignState.chooseTemplateButtonsData[0]

            // Remove surrounding brackets from button data
            text = text.substring(2, text.length - 1);

            let matches = String(text).match(regex);
            let content = [];
            let srtButton = "";
            const words = String(text).split(',')
            // content.push(words[0]);
            // content.push(words[1]);

            if (matches) {
                let currentIndex = 0;
                matches.forEach(matchValue => {
                    const number = parseInt(matchValue.match(/\d+/)[0], 10);
                    const matchIndex = text.indexOf(matchValue);
                    const outsideText = text.slice(currentIndex, matchIndex);
                    content.push((<span >{outsideText}</span>));
                    srtButton += outsideText;
                    // component.push((<span className=" text-danger  font-size-md" >{buttonVariable}</span>));
                    content.push((<span className=" text-danger  font-size-md">test</span>));
                    srtButton += buttonVariable;
                    currentIndex = matchIndex + matchValue.length;
                });


                // Eğer metin, son eşleşme sonrasında devam ediyorsa, kalan kısmı da ekrana yazdır
                if (currentIndex < text.length) {
                    const remainingText = text.slice(currentIndex);
                    content.push((<span>{remainingText}</span>));
                    srtButton += remainingText;

                }
            }
            else {
                // Eğer eşleşme bulunamadıysa, metnin tamamını dışındaki metin olarak kabul edebilirsiniz.
                content.push((<span>{text}</span>));
                srtButton += text;


            }
            const word = String(srtButton).split(',');

            return (
                <Tooltip placement="bottom" color={""} title={word[1]}>
                    <div className=' mr-2 p-1 d-flex align-items-center justify-content-center' style={{ background: "#d3eebe", borderRadius: 5 }} >
                        <a >{word[0]}</a>
                    </div>
                </Tooltip>
            )
        }
        else {//değişken yoksa
            let text = campaignState.chooseTemplateButtonsData

            const response = text.map(item => {


                const text = removeBrackets(item);
                const words = String(text).split(',')
                if (words.length > 1)//Linkli olan
                {

                    return <Tooltip placement="bottom" title={words[1]}>
                        <div className=' mr-2 p-1 d-flex align-items-center justify-content-center' style={{ background: "#d3eebe", borderRadius: 5 }} >
                            <a>{words[0]}</a>
                        </div>
                    </Tooltip>
                }
                else//c2a buton
                {
                    return (
                        <div className="hover-button mt-2 mr-3 ml-2 " style={{ alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontWeight: "bold" }}>{text}</span>
                        </div>
                    );
                }

            })
            return response

        }
    }
    /**
     * Finds parameter id by provided id within `parameterData`.
     *
     * @param {string|number} parameterId - Parameter identifier
     * @returns {string|number|undefined} Matching parameter id
     */
    const getCampaignParameter = (parameterId) => {

        var index = parameterData.findIndex(w => w.parameterid == parameterId);


        return parameterData[index]?.parameterid
    }
    /**
     * Resolves parameter display name by id.
     *
     * @param {string|number} parameterId - Parameter identifier
     * @returns {string|undefined} Parameter display name
     */
    const getCampaignParameterName = (parameterId) => {

        var index = parameterData.findIndex(w => w.parameterid == parameterId);


        return parameterData[index]?.parametername
    }
    /**
     * Gets group name by `groupId` from `toData`.
     *
     * @param {string|number} groupId
     * @returns {string} Group name
     */
    const getCampaignTo = (groupId) => {

        var index = toData.findIndex(w => w.groupid == groupId);
        return toData[index].groupname
    }
    /**
     * Gets branch display value by key from `branchList`.
     *
     * @param {string|number} groupId - Branch key
     * @returns {string} Branch name
     */
    const getCampaignBranchName = (groupId) => {

        var index = branchList.findIndex(w => w.key == groupId);
        return branchList[index].value
    }
    /**
     * Gets customer name by id from `customerList`.
     *
     * @param {string|number} clientId
     * @returns {string} Customer name
     */
    const getCampaignCustomers = (clientId) => {

        var index = customerList.findIndex(w => w.clientId == clientId);
        return customerList[index].clientName
    }
    /**
     * Gets event name by id from `eventCustomerList`.
     *
     * @param {string|number} eventId
     * @returns {string|undefined} Event name
     */
    const getCampaignEventName = (eventId) => {

        var index = eventCustomerList.findIndex(w => w.eventId == eventId);
        return eventCustomerList[index]?.eventName
    }
    /**
     * Gets stand name by id from `standCustomerList`.
     *
     * @param {string|number} eventId - Stand id
     * @returns {string|undefined} Stand name
     */
    const getCampaignStandName = (eventId) => {

        var index = standCustomerList.findIndex(w => w.standId == eventId);
        return standCustomerList[index]?.standName
    }
    /**
     * Calculates total recipient count based on current selection and app type.
     * Considers events, sessions, branches, direct selections, and split groups.
     *
     * @returns {number} Total recipient count
     */
    const getCampaignGrupTotalCount = () => {
        var count = 0;
        if (selectedCustomerGroupType == customerGroupTypes.eventCustomers) {
            if (campaignState.campaignStandCustomers.length > 0) {
                campaignState.campaignStandCustomers.map((item, index) => {
                    var index = standCustomerList.findIndex(w => w.standId == item);
                    count += standCustomerList[index].customerCount;
                });
            } else {
                campaignState.campaignEventCustomers.map((item, index) => {
                    var index = eventCustomerList.findIndex(w => w.eventId == item);
                    count += eventCustomerList[index].customerCount;
                });
            }

        } else if (selectedCustomerGroupType == customerGroupTypes.sessionCustomers) {

            if (activeSessionClientIds?.length == 0) {
                return 0;
            }

            if (isCoffee && campaignState.campaignBranchs?.length > 0) {
                for (const element of campaignState.campaignBranchs) {
                    var index = branchList.findIndex(w => w.key == element);
                    const branchClients = branchList[index].customerIds;
                    const activeSessionClients = activeSessionClientIds;

                    const commonClients = branchClients.filter(clientId => activeSessionClients.includes(clientId));

                    count += commonClients.length;

                }
            } else {
                return activeSessionClients.length;
            }


        } else if (selectedCustomerGroupType == customerGroupTypes.customerSelect) {

            return campaignState.campaignToCustomers.length;
        } else if (selectedCustomerGroupType == customerGroupTypes.splitedCustomers) {
            return campaignState.campaignToSplitedCustomers.length;
        } else {


            if (isCoffee && campaignState.campaignBranchs?.length > 0) {

                for (const element of campaignState.campaignBranchs) {
                    var index = branchList.findIndex(w => w.key == element);
                    const branchClients = branchList[index].customerIds;

                    if (campaignState.campaignTo?.length > 0) {
                        for (const groupElement of campaignState.campaignTo) {

                            if (groupElement == -1) { //All customers
                                count += branchClients?.length;
                            } else {
                                const groupIndex = toData.findIndex(w => w.groupid == groupElement);


                                const groupClients = toData[groupIndex].customerids;

                                const commonClients = branchClients.filter(clientId => groupClients.includes(clientId));

                                count += commonClients.length;
                            }

                        }
                    } else {
                        count += branchClients?.length;
                    }

                }
            } else {
                campaignState.campaignTo.map((item, index) => {
                    var index = toData.findIndex(w => w.groupid == item);
                    count += toData[index].customercount;
                });
            }
        }
        return count;
    }

    /**
     * Handles changes to template variable content
     * Updates the variable array when user modifies template parameters
     * 
     * @param {number} i - Index of the variable being changed
     * @param {string} value - New value for the variable
     */
    const handleContentChange = (i, value) => {
        let tmpVariable = [...variable];
        tmpVariable[i] = value;
        setVariable(tmpVariable);

        // Legacy code for direct campaign state update (commented out)
        // try {
        //     setCampaignState({
        //         ...campaignState,
        //         chooseTemplateContent: value,
        //     })
        //     variable[i]:value;
        // } catch (error) {
        //     alert(error);
        // }
    };
    /**
     * Formats phone number with standard US format
     * Removes non-digit characters and applies (XXX) XXX-XXXX formatting
     * 
     * @param {string} phone - Raw phone number input
     * @returns {string} Formatted phone number
     */
    const formatPhoneNumber = (phone) => {
        // Remove all non-digit characters
        let cleaned = phone.replace(/\D/g, '');

        // Apply formatting for 10-digit numbers
        if (cleaned.length <= 10) {
            cleaned = cleaned.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
        }

        return cleaned;
    };
    /**
     * Handles test phone number input changes
     * Manages both formatted display and raw number for API calls
     * 
     * @param {Event} e - Input change event
     */
    const testPhoneChange = (e) => {
        const value = e.target.value;
        const formattedPhone = formatPhoneNumber(value);
        setTestPhone(formattedPhone);

        // Store only digits for API submission
        const rawValue = value.replace(/\D/g, '');
        setRawPhone(rawValue);  // Raw phone contains only numbers
        setTestPhone(value);

        // Alternative implementation (commented out)
        // Store raw value directly
        setRawPhone(value);
    };
    /**
     * Sends a test message to the specified phone number
     * Allows users to preview how the campaign message will look before sending to all recipients
     * 
     * @async
     * @function sendTestTemplateMessage
     */
    const sendTestTemplateMessage = async () => {
        setLoadingPanel(true);

        // Prepare test message parameters
        var params = {
            campaignName: campaignState.campaignName,
            firmBusinessNumberId: getCampaignRow().firmbusinessnumberid,
            firmBusinessNumber: getCampaignRow().businessnumber,
            groupIds: campaignState.campaignTo,
            templateId: campaignState.chooseTemplateId,
            templateType: campaignState.chooseTemplateType,
            message: sendMessage(),
            parameterIds: variable.length == 0 ? null : variable,
            campaignStatus: 1,
            buttonValue: buttonVariable,
            testPhone: rawPhone,  // Raw phone number for API
            mediaId: campaignState.mediaId,
        }

        // Send test message via API
        var result = await network.post("/TemplateMessage/SendTestTemplateMessage", params);
        setLoadingPanel(false);

        if (result.isSuccess) {
            var postResult = result;
            if (postResult.isSuccess) {
                message.success(t('messageSentSuccessfully'));
            } else {
                alert(postResult.errorMessage);
            }
        } else {
            alert(result.errorMessage);
        }
    }
    /**
     * Confirmation Modal Component
     * Displays campaign details before final sending
     * Shows recipient count, target groups, and message preview
     * 
     * @param {Object} props - Modal properties
     * @param {boolean} props.visible - Modal visibility state
     * @param {Function} props.onClose - Modal close handler
     * @param {string} props.senderNumber - Sender phone number
     * @param {Array} props.groups - Target customer groups
     * @param {string} props.messageContent - Message content preview
     * @returns {JSX.Element} Confirmation modal component
     */
    const ConfirmationModal = ({ visible, onClose, senderNumber, groups, messageContent }) => {
        return (
            <Modal
                centered
                visible={visible}
                onOk={onClose}
                onCancel={onClose}
                footer={null}
            >
                {/* Modal Title */}
                <div style={{ marginBottom: "15px" }}>
                    <h2 className='ant-typography' strong style={{ color: "#555555" }}>
                        {t('campaignSendConfirmationTitle')}
                    </h2>
                </div>


                <div style={{ marginBottom: "15px" }}>

                    <Text style={{
                        fontSize: 17
                    }}>
                        {t("campaignSendConfirmationFirst")} <span style={{ color: "#2a59d1", fontWeight: "bold" }} >{getCampaignGrupTotalCount()}</span> {t("campaignSendConfirmationSecond")}
                    </Text>

                    <div style={{ marginBottom: "15px", marginTop: 15 }} >

                        <span style={{ color: 'gray', marginBottom: 10 }}> {t('targetGroups')} </span>
                    </div>
                    {selectedCustomerGroupType == customerGroupTypes.sessionCustomers ? (
                        <Alert message={t("sessionMessageInfo").replace("{count}", getCampaignGrupTotalCount())}
                            type={"info"} >
                        </Alert>
                    ) : selectedCustomerGroupType == customerGroupTypes.customerSelect ?
                        campaignState.campaignToCustomers.map((item) => {

                            return (
                                <Tag className="m-1" color={"blue"} key={item}>
                                    <div style={{ fontSize: "1rem", color: "#1890ff" }}>
                                        {getCampaignCustomers(item)}
                                    </div>
                                </Tag>
                            );
                        }) : (
                            <>
                                {
                                    groups.map((item) => {
                                        return (
                                            <Tag className="m-1" color={"blue"} key={item.value}>
                                                <div style={{ fontSize: "1rem", color: "#1890ff" }}>
                                                    {item.label}
                                                </div>
                                            </Tag>
                                        );
                                    })
                                }
                                {campaignState.campaignBranchs?.length > 0 && campaignState.campaignBranchs.map((item, index) => {
                                    return (
                                        <Tag className="m-1" color={"blue"} key={item.value}>
                                            <div style={{ fontSize: "1rem", color: "#1890ff" }}>
                                                {getCampaignBranchName(item)}
                                            </div>
                                        </Tag>
                                    );
                                })}
                            </>
                        )}

                </div>

                {/* Mesaj İçeriği */}
                <div className='mt-5'>
                    <ReusableButton
                        variant="campaign"
                        text={t('send')}
                        onClick={() => {
                            controlCredit();
                        }}
                        icon={<SendOutlined />}
                    />
                </div>

            </Modal>
        );
    };

    const getCampaignParams = () => {

        let groupIds = campaignState.campaignTo;
        let groupType = customerGroupTypes.customerGroups;
        if (selectedCustomerGroupType == customerGroupTypes.eventCustomers) {
            if (campaignState.campaignStandCustomers?.length > 0) {
                groupType = customerGroupTypes.standCustomers;
                groupIds = campaignState.campaignStandCustomers;
            } else {
                groupType = customerGroupTypes.eventCustomers;
                groupIds = campaignState.campaignEventCustomers
            }
        } else if (selectedCustomerGroupType == customerGroupTypes.sessionCustomers) {
            groupType = customerGroupTypes.sessionCustomers;
            groupIds = [];
        } else if (selectedCustomerGroupType == customerGroupTypes.customerSelect) {
            groupType = customerGroupTypes.customerSelect;
            groupIds = campaignState.campaignToCustomers;
        } else if (selectedCustomerGroupType == customerGroupTypes.splitedCustomers) {
            groupType = customerGroupTypes.splitedCustomers;
            groupIds = campaignState.campaignToSplitedCustomers;
        }
        var params = {
            campaignName: campaignState.campaignName,
            firmBusinessNumberId: getCampaignRow().firmbusinessnumberid,
            firmBusinessNumber: getCampaignRow().businessnumber,
            groupIds: groupIds,
            branchIds: campaignState.campaignBranchs,
            templateId: campaignState.chooseTemplateId,
            templateType: campaignState.chooseTemplateType,
            message: sendMessage(),
            parameterIds: variable.length == 0 ? null : variable,
            campaignStatus: 1,
            buttonValue: buttonVariable,
            mediaId: campaignState.mediaId,
            campaignGroupType: groupType,
            splitgroupname: selectedCustomerGroupType === customerGroupTypes.splitedCustomers ?
                `Group ${splitedCustomerList.findIndex(group =>
                    JSON.stringify(group.map(c => c.clientId)) === JSON.stringify(campaignState.campaignToSplitedCustomers)
                ) + 1}` : "",
        }
        return params;
    }
    const controlCredit = async () => {
        setModalVisible(false);
        setLoadingPanel(true);
        if (selectedCustomerGroupType == customerGroupTypes.sessionCustomers) {
            sendCampaign();
        } else {

            const params = getCampaignParams();
            var result = await network.post("/admin/Campaign/control-packages", params);

            if (result.isSuccess) {
                sendCampaign();
            } else if (limits <= 0) {
                message.error(t("dailyLimitWouldBeExceeded").replace("{limit}", dailyUsage));
            }
            else {
                message.error(t("creditOverflowApiError"))
            }
        }

        setLoadingPanel(false);


    }
    const sendCampaign = async () => {

        // setLoadingPanel(true);

        const params = getCampaignParams();
        // var result = 
        network.post("/TemplateMessage/SendTemplateMessageAndCreateCampaign", params);

        message.success(t('messageSentSuccessfully'));
        setTimeout(() => {
            navigate('/campaigns')
        }, 250);
    }
    const onFinish = async () => {

        if (selectedCustomerGroupType == customerGroupTypes.sessionCustomers && getCampaignGrupTotalCount() == 0) {
            message.error(t("noOpenedSessionMessage"));
            return;
        }
        setLoadingPanel(true);
        var error = false;
        setCampaignNameValidation(false);
        setFromValidation(false);
        setToValidation(false);
        setChooseTemplateValidation(false);
        setContentValidation(false);


        if (campaignState.campaignName == "") {
            setCampaignNameValidation(true);
            setInput(true);
            error = true;


        }
        if (getCampaignRow().firmbusinessnumberid == undefined) {
            setFromValidation(true);
            error = true;


        }
        if (campaignState.chooseTemplateType == customerGroupTypes.customerGroups && campaignState.campaignTo.length == 0) {
            setToValidation(true);
        } else if (campaignState.chooseTemplateType == customerGroupTypes.eventCustomers && campaignState.campaignEventCustomers.length == 0) {
            setToValidation(true);
        } else if (campaignState.chooseTemplateType == customerGroupTypes.customerSelect && campaignState.campaignToCustomers.length == 0) {
            setToValidation(true);
        }
        if (campaignState.chooseTemplateId == "") {
            setChooseTemplateValidation(true);
            error = true;
        }

        if (error == true) {
            api['error']({
                message: t('campaignNotSendError'),
                description:
                    t('campaignNotSendError2'),
            });

            setLoadingPanel(false);

        } else {
            setLoadingPanel(false);
            setModalVisible(true);
        }
    }
    /**
     * Renders a dynamic parameter select list for template variables.
     *
     * @param {number} value - Index to return from the generated list
     * @returns {JSX.Element} A Select component for the specified variable index
     */
    const DynamicSelectList = (value) => {
        let list = [];
        console.log("DYNAMIC COUNT", campaignState.chooseTemplateDynamicCount);

        const index = campaignState.chooseTemplateDynamicCount ? campaignState.chooseTemplateDynamicCount : 0;
        for (let i = 0; i < index; i++)
            list.push(
                <Select
                    style={{
                        width: "130px",
                        height: "30px",
                        marginTop: "10px",
                        marginBottom: "15px"
                    }}
                    onChange={(value, label) => {
                        handleContentChange(i, value, label);
                    }}>
                    {parameterData.map((item, index) => (
                        <Option value={item.parameterid} >
                            {item.parametername}
                        </Option>
                    ))}
                </Select>

            );
        return list[value];
    };
    /**
     * Retrieves selected sender row data from `fromData` by `campaignFrom`.
     *
     * @returns {Object} Sender row or empty object
     */
    const getCampaignRow = () => {
        let index = fromData.findIndex(w => w.firmbusinessnumberid == campaignState.campaignFrom);

        if (index != -1) {
            return fromData[index];
        }
        return {};
    }


    /**
     * Updates selected sender (from) business number.
     *
     * @param {string|number} value - Selected sender id
     * @param {any} key - AntD Select extra argument (unused)
     */
    const handleFromChange = (value, key) => {
        try {
            setCampaignState({
                ...campaignState,
                campaignFrom: value
            })

        } catch (error) {
            alert(error);
        }

    };
    /**
     * Handles changes to customer group selection
     * Updates campaign state with selected groups and their names
     * 
     * @param {Array} value - Array of selected group IDs
     * @param {Array} label - Array of selected group names
     */
    const handleToChange = (value, label) => {
        try {
            setCampaignState({
                ...campaignState,
                campaignTo: value,
                campaignToName: label
            })
        } catch (error) {
            alert(error);
        }
    };
    /**
     * Handles changes to branch selection for coffee shop campaigns
     * Updates campaign state with selected branch IDs
     * 
     * @param {Array} value - Array of selected branch IDs
     * @param {Array} label - Array of selected branch names (unused)
     */
    const handleBranchChange = (value, label) => {
        try {
            setCampaignState({
                ...campaignState,
                campaignBranchs: value,
            })
        } catch (error) {
            alert(error);
        }
    };
    /**
     * Handles changes to individual customer selection
     * Updates campaign state with selected customer IDs
     * 
     * @param {Array} value - Array of selected customer IDs
     */
    const handleClientSelectChange = (value) => {
        try {
            setCampaignState({
                ...campaignState,
                campaignToCustomers: value,
            })
        } catch (error) {
            alert(error);
        }
    };
    /**
     * Handles changes to segmented customer selection
     * Updates campaign state with selected segmented customer data
     * 
     * @param {Object} option - Selected option containing customer data
     * @param {Array} option.data - Array of customer IDs in the selected segment
     */
    const handleChangeSplited = (option) => {
        try {
            setCampaignState({
                ...campaignState,
                campaignToSplitedCustomers: option.data
            })
        } catch (error) {
            alert(error);
        }
    };
    /**
     * Handles changes to event customer selection
     * Updates campaign state with selected event IDs and names
     * 
     * @param {Array} value - Array of selected event IDs
     * @param {Array} label - Array of selected event names
     */
    const handleeventCustomerChange = (value, label) => {
        try {
            setCampaignState({
                ...campaignState,
                campaignEventCustomers: value,
                campaignEventCustomersName: label
            })
        } catch (error) {
            alert(error);
        }
    };
    /**
     * Handles changes to stand customer selection
     * Updates campaign state with selected stand IDs and names
     * 
     * @param {Array} value - Array of selected stand IDs
     * @param {Array} label - Array of selected stand names
     */
    const handleStandCustomerChange = (value, label) => {
        try {
            setCampaignState({
                ...campaignState,
                campaignStandCustomers: value,
                campaignStandCustomersName: label
            })
        } catch (error) {
            alert(error);
        }
    };
    /**
     * Fetches all available message templates and parameters from the API
     * Loads template data for user selection and parameter data for variable substitution
     * 
     * @async
     * @function getAllTemplate
     */
    const getAllTemplate = async () => {
        // Fetch active message templates
        var result = await network.get("/TemplateMessage/GetAllActiveTemplate", false);
        if (!result.isSuccess) {
            message.error(result.errorMessage);
        }
        else {
            if (!result.data.isSuccess) {
                message.error(result.data.errorMessage);
            } else {
                setTemplatesData(result.data.data);
                console.log("TEMPLATES", result.data.data)
                setFilteredTemplates(result.data.data);
            }
        }

        // Fetch available parameters for templates
        var resultParameter = await network.get("/admin/Parameter/selectParameterList", false);

        console.log("Result parameter", resultParameter)
        if (!resultParameter.isSuccess) {
            message.error(result.errorMessage);
        }
        else {
            if (!resultParameter.data.isSuccess) {
                message.error(result.data.errorMessage);
            } else {
                setParameterData(resultParameter.data.data);
            }
        }

    }

    const responseFromData = async () => {
        try {
            setIsLimitLoading(true);
            const [fromResult, toResult, limitsResult] = await Promise.all([
                network.get("/admin/Firm/selectFirmNumberList", false),
                network.get("/admin/Group/selectGroupList-campaign", false),
                network.get("/admin/campaign/get-limits")
            ]);

            const fromData = fromResult.data.data;
            setFromData(fromData);
            setCampaignState({
                ...campaignState,
                campaignFrom: fromData[0].firmbusinessnumberid
            });

            const groupData = toResult.data.data;
            if (groupData) {
                const groupList = groupData.groupList;
                const customerCount = groupData.customerCount;
                groupList.push({
                    groupname: t("allReceiver"),
                    groupdesc: t("allReceiver"),
                    groupid: -1,
                    status: true,
                    customercount: customerCount
                });

                setToData(groupList);

                const slicedCustomerArray = [];
                var limit = 0;

                const sentMessageCount = dailyUsage?.data?.totalSentMessage || 0;
                if (limitsResult.isSuccess) {
                    console.log("limitsResult.data.data.currentLimit", limitsResult.data.data.currentLimit)
                    if (limitsResult.data.data.currentLimit == "TIER_1K") {
                        limit = LIMIT_TIER_1K - sentMessageCount;
                    }
                    else if (limitsResult.data.data.currentLimit == "TIER_2K") {
                        limit = LIMIT_TIER_2K - sentMessageCount;
                    }
                    else if (limitsResult.data.data.currentLimit == "TIER_10K") {
                        limit = LIMIT_TIER_10K - sentMessageCount;
                    } else if (limitsResult.data.data.currentLimit == "TIER_100K") {
                        limit = LIMIT_TIER_100K - sentMessageCount;
                    }
                    console.log("limitsResult.data.data.currentLimit", limit)
                    setLimits(limit);
                }

                // Filter customers from last 7 days
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

                // const recentCustomers = groupData.clientList.filter(customer => {
                //     const customerDate = new Date(customer.insertDate);
                //     return customerDate >= sevenDaysAgo;
                // });

                // // Add recent customers as first group if there are any
                // if (recentCustomers.length > 0) {
                //     slicedCustomerArray.push(recentCustomers);
                // }

                // Add regular sliced groups
                if (limit !== 0) {
                    for (let i = 0; i < groupData.clientList.length; i += limit) {
                        slicedCustomerArray.push(groupData.clientList.slice(i, i + limit));
                    }
                }

                setSplitedCustomerList(slicedCustomerArray);


                setCustomerList(groupData.clientList);
                setActiveSessionClients(groupData.activeSessionClientNumbers);
                setActiveSessionClientIds(groupData.activeSessionClientIds);
                setBranchList(groupData.allBranch);
            }
            if (isEvent) {
                const eventResult = await network.get("/admin/event/campaign-events");
                if (eventResult.isSuccess && eventResult.data) {
                    const eventData = eventResult.data;
                    setEventCustomerList(eventData.eventCustomerList);
                    setStandCustomerList(eventData.standCustomerList);
                }
            }
        } finally {
            setIsLimitLoading(false);
        }
    }
    /**
     * Tracks dynamic button input changes.
     *
     * @param {Event} e - Input change event
     */
    const inputChange = (e) => {
        if (e.target.value) {
            setButtonVariable(e.target.value)
        }
    };
    /**
     * Handles campaign name input and enables editing state.
     *
     * @param {Event} e - Input change event
     */
    const onCampNameInput = (e) => {

        setInputEnable(true);

        setInputValue(e.target.value);

    };
    /**
     * Confirms campaign name and disables input when non-empty.
     */
    const InputTrueClick = () => {
        if (inputValue != "") {
            setInput(false);
        }
        setCampaignState({
            ...campaignState,
            campaignName: inputValue
        })
        // setCampaignName(inputValue)

        setInputEnable(false);
    };
    const getTemplateTypeStyle = (type) => {
        switch (type) {
            case 'TEXT':
                return { backgroundColor: '#f7faff', color: '#0078D4', padding: '5px 10px', borderRadius: '5px' }; // Hafif mavi ton
            case 'IMAGE':
                return { backgroundColor: '#fff7e6', color: '#fa8c16', padding: '5px 10px', borderRadius: '5px' }; // Turuncu ton
            case 'VIDEO':
                return { backgroundColor: '#fcefe6', color: '#d4380d', padding: '5px 10px', borderRadius: '5px' }; // Kırmızımsı ton
            default:
                return { backgroundColor: '#f0f0f0', color: '#595959', padding: '5px 10px', borderRadius: '5px' }; // Nötr gri
        }
    };
    const [columns, setColumns] = useState([
        {
            key: 1,
            title: t('templateName'),
            dataIndex: 'elementName',
            width: 100,
            colSpan: 1,
            render: (_, record) => (
                <div style={styles.cell}>
                    <div style={styles.text}>{record.elementName}</div>
                </div>
            )
        },
        {
            key: 2,
            title: t('templateType'),
            dataIndex: 'templateType',
            width: 100,
            render: (_, record) => {
                // Template tipine göre renklendirme
                const typeStyle = getTemplateTypeStyle(record.templateType);
                return (
                    <div style={styles.cell}>
                        <div style={{ ...styles.text, ...typeStyle }}>{record.templateType}</div>
                    </div>
                );
            }
        },
        {
            key: 2,
            title: <div style={{ textAlign: 'left', marginLeft: 8 }}>{t('messageContent')}</div>,
            dataIndex: 'data',
            width: 350,
            render: (_, record) => (
                <div style={styles.cell}>
                    <div style={{
                        backgroundColor: "#DCF8C6", // Gönderilen mesajlar için WhatsApp yeşili
                        borderRadius: "12px", // Yuvarlak köşeler
                        padding: "10px 15px", // Mesaj içeriği için boşluk
                        color: "#111111", // Yazı rengi
                        fontWeight: 400, // Yazı kalınlığı
                        maxWidth: "80%", // Mesajın genişliği
                        wordWrap: "break-word", // Uzun mesajları kır
                        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)", // Hafif gölge
                        display: "inline-block", // İçeriği bir satırda tut
                        margin: "5px 0", // Mesaj balonları arası boşluk
                        alignSelf: "flex-end", // Gönderilen mesajı sağa hizala
                    }}>
                        {record.data}
                    </div>
                </div>
            )
        },
    ])
    const templateTypeFor = () => {

        if (campaignState.chooseTemplateType == "IMAGE") {
            return <div align="center" className='mb-1' >
                <img style={{ borderRadius: 7 }} src="https://i.hizliresim.com/7kb2hsj.png" width={"100%"} height={"100%"} />
            </div>
        }
        else if (campaignState.chooseTemplateType == "VIDEO")
            return <div align="center" className='mb-1' >
                <img style={{ borderRadius: 7 }} src="https://i.hizliresim.com/7kb2hsj.png" width={"100%"} height={"100%"} />
            </div>
        else if (campaignState.chooseTemplateType == "LOCATION") {
            return <div><img style={{ borderRadius: 7 }} src="https://i.hizliresim.com/7kb2hsj.png" width={"100%"} height={"100%"} /></div>
        };

    };
    const CampaignInput = () => (
        <Row align="middle" justify="space-between" className='mb-4 mt-2'>
            <Col span={24}>
                <Form.Item
                    rules={[
                        {
                            required: true,
                            message: 'Please input your username!',
                        },
                    ]}
                    style={{ marginBottom: 0 }}
                >
                    <div>
                        <h3>{t('newCampaignName')}</h3>
                    </div>
                    <Row gutter={16} align="middle">
                        <Col span={8}>
                            <Input
                                placeholder={t('enterCampaginName')}
                                onChange={onCampNameInput}
                                defaultValue={campaignState.campaignName}
                                showCount
                                maxLength={20}
                                className={campaignNameValidation ? panelErrorStyle : ""}
                                style={{ width: '100%', cursor: "text" }}
                                disabled={!inputEnable}

                            />
                        </Col>
                        <Col span={2}>
                            {inputEnable ? (
                                <Button onClick={() => InputTrueClick()}>
                                    {<CheckOutlined style={{ fontSize: "18px" }} />}
                                </Button>
                            ) : (
                                <Button onClick={() => setInputEnable(true)}>
                                    <EditOutlined />
                                </Button>
                            )}
                        </Col>
                        <Col span={14}>
                            <Row gutter={16} justify="middle" align="middle">
                                <Col span={10}>
                                    <ReusableButton
                                        variant="template"
                                        text={t('sendCampaign')}
                                        onClick={() => {
                                            onFinish();
                                        }}
                                        icon={<SendOutlined />}
                                    />
                                </Col>
                                <Col>
                                    {isLimitLoading ? (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#fff',
                                            padding: '6px 16px',
                                            borderRadius: '6px',
                                            border: '1px solid #f0f0f0',
                                            minWidth: '180px',
                                            minHeight: "40px"
                                        }}>
                                            <Spin size="small" />
                                            <Text style={{ marginLeft: '12px', fontSize: '14px' }}>
                                                {t("loadingLimit")}
                                            </Text>
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#f6ffed',
                                            padding: '6px 16px',
                                            borderRadius: '10px',
                                            border: '1px solid #b7eb8f',
                                            minWidth: '180px',
                                            minHeight: "40px"
                                        }}>
                                            <Badge status="processing" />
                                            <Text style={{ marginLeft: '12px', fontSize: '14px' }}>
                                                {t('remaining')}: <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{typeof limits === 'number' ? limits : 0}</span>
                                            </Text>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Form.Item>
            </Col>
        </Row>
    );


    const headerFromExtra = () => (
        <div>
            <Row>
                <Col span={1} className='mr-2'>
                    <div>
                        <Row className='ml-1 mt-2'>
                            {campaignState.campaignFrom != "" ? <CheckCircleFilled style={{ fontSize: "24px", color: 'black' }} /> : <CheckCircleFilled style={{ fontSize: "24px", color: '#dbd9d2' }} />}
                        </Row>
                    </div>
                </Col>
                <Row>
                    <Col >
                        <div>
                            <h3>{t('sender')}</h3>
                        </div>
                        <div>
                            {t('numberToSend')}
                        </div>
                    </Col>
                </Row>
            </Row>
            <Row className='mt-2 ml-1'>
                <Col span={1}>
                </Col >
                <Tag align="left" color="green">{getCampaignRow().friendlyname}</Tag>

            </Row>
        </div>
    );
    const headerToExtra = () => (
        <div>
            <Row >
                <Col span={1} className='mr-2' >
                    <div>
                        <Row className='ml-1 mt-2'>
                            {campaignState.campaignTo != "" ? <CheckCircleFilled style={{ fontSize: "24px", color: 'black' }} /> : <CheckCircleFilled style={{ fontSize: "24px", color: '#dbd9d2' }} />}
                        </Row>
                    </div>


                </Col>
                <Row>
                    <Col >
                        <div>
                            <h3>{t('recipient')}</h3>
                        </div>
                        <div>
                            {t('whichGroupsSendMessage')}
                        </div>
                    </Col>
                </Row>
            </Row>
            <Row className="mt-2"  >
                <Col span={1}>
                </Col >
                <Col span={16}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {selectedCustomerGroupType == customerGroupTypes.eventCustomers ?
                            (
                                <>
                                    {campaignState.campaignStandCustomers?.length > 0 ? campaignState.campaignStandCustomers.map((item, index) => (
                                        <Tag key={index} className="m-1" color={"blue"}>
                                            {getCampaignStandName(item)}
                                        </Tag>
                                    )) : campaignState.campaignEventCustomers.map((item, index) => (
                                        <Tag key={index} className="m-1" color={"blue"}>
                                            {getCampaignEventName(item)}
                                        </Tag>
                                    ))}



                                </>
                            ) : selectedCustomerGroupType == customerGroupTypes.sessionCustomers ? (
                                <Tag className="m-1" color={"blue"}>
                                    {t("sessionCustomerList")}
                                </Tag>
                            ) : selectedCustomerGroupType == customerGroupTypes.customerSelect ? campaignState.campaignToCustomers.map((item, index) => (
                                <Tag key={index} className="m-1" color={"blue"}>
                                    {getCampaignCustomers(item)}
                                </Tag>
                            ))
                                : (
                                    <>
                                        {
                                            campaignState.campaignTo.map((item, index) => (
                                                <Tag key={index} className="m-1" color={"blue"}>
                                                    {getCampaignTo(item)}
                                                </Tag>
                                            ))
                                        }
                                        {isCoffee && campaignState.campaignBranchs?.length > 0 &&
                                            campaignState.campaignBranchs.map((item, index) => (
                                                <Tag key={index} className="m-1" color={"blue"}>
                                                    {getCampaignBranchName(item)}
                                                </Tag>
                                            ))}
                                    </>
                                )

                        }
                    </div>
                </Col>


            </Row>
        </div>

    );
    const headerChooseTempExtra = () => (
        <div>
            <Row>
                <Col span={1} className='mr-2'>
                    <div>
                        <Row className='ml-1 mt-2'>

                            {campaignState.chooseTemplateMessage != "" ? <CheckCircleFilled style={{ fontSize: "24px", color: 'black' }} /> : <CheckCircleFilled style={{ fontSize: "24px", color: '#dbd9d2' }} />}

                        </Row>
                    </div>
                </Col>
                <Row>
                    <Col >
                        <div>
                            <h3>{t('messageTemplate')}</h3>
                        </div>
                        <div>
                            {t('templateInfo')}
                        </div>
                    </Col>
                </Row>

            </Row>
            <Row className='mt-2 ml-1'>
                <Col span={1}>
                </Col >
                <Tag align="left" color="yellow">{campaignState.chooseTemplateName}</Tag>
            </Row>
        </div>

    );
    const headerContentExtra = () => (
        <div>
            <Row>
                <Col span={1} className='mr-2'>
                    <div>
                        <Row className='ml-1 mt-2'>
                            {(campaignState.chooseTemplateId != "" && variable.length == campaignState.chooseTemplateDynamicCount) ? <CheckCircleFilled style={{ fontSize: "24px", color: 'black' }} /> : <CheckCircleFilled style={{ fontSize: "24px", color: '#dbd9d2' }} />}
                        </Row>
                    </div>
                </Col>
                <Row>
                    <Col >
                        <div>
                            <h3>{t('content')}</h3>
                        </div>
                        <div>
                            {t('designContentMessage')}
                        </div>
                    </Col>
                </Row>

            </Row>


        </div>

    );
    const testContentExtra = () => (
        <div>
            <Row>
                <Col span={1} className='mr-2'>
                    <div>
                        <Row className='ml-1 mt-2'>
                            {(campaignState.chooseTemplateId != "" && variable.length == campaignState.chooseTemplateDynamicCount) ? <CheckCircleFilled style={{ fontSize: "24px", color: 'black' }} /> : <CheckCircleFilled style={{ fontSize: "24px", color: '#dbd9d2' }} />}
                        </Row>
                    </div>
                </Col>
                <Row>
                    <Col >
                        <div>
                            <h3>{t('testMessage')}</h3>
                        </div>
                        <div>
                            {t('testMessageInfo')}
                        </div>
                    </Col>
                </Row>

            </Row>


        </div>

    );
    const genFromExtra = () => (
        <div>
            <Row className='mt-3 mr-3'>

                <UserOutlined style={{ fontSize: "24px" }} />
            </Row>
        </div>

    );

    const isOverflowCredit = () => {

        const totalRecipent = getCampaignGrupTotalCount();
        if (totalRecipent > 0 && (totalRecipent > totalCredit)) {
            return true;
        }
        return false;
    }

    const genToExtra = () => {
        const isCreditOverflow = isOverflowCredit();
        let overflowColorStyle = {
        }
        if (isCreditOverflow) {
            overflowColorStyle = {
                color: "red"
            }
        }

        return (
            <Col className='mt-3 mr-3' style={{}}>
                <Row style={{ marginBottom: 20, justifyContent: 'end', alignItems: 'center' }}>
                    <UsergroupAddOutlined style={{ fontSize: "24px" }} />
                </Row>
                <Row className='' style={{ marginTop: 10, justifyContent: 'end', alignItems: 'center' }}>
                    <div
                        style={{
                            fontStyle: "normal",
                            color: "#000000",
                            fontWeight: "600",
                            padding: "6px 8px",
                            fontSize: 16,
                            ...overflowColorStyle
                        }}
                    >
                        {t('totalBuyerCount')}:{" "}
                        <span className='wa-color' style={{ fontWeight: "bold", ...overflowColorStyle }}>
                            {getCampaignGrupTotalCount()}
                        </span>
                    </div>
                </Row>
                {isCreditOverflow && (
                    <Tag color='error'>
                        {t("creditOverflow").replace("{total}", totalCredit)}
                    </Tag>
                )}
            </Col>
        )
    };
    const genChooseTempExtra = () => (
        <div>
            <Row className='mt-3 mr-3'>
                <UnorderedListOutlined style={{ fontSize: "24px" }} />
            </Row>
        </div>

    );
    const genContentExtra = () => (
        <div>
            <Row className='mt-3 mr-3'>
                <FontSizeOutlined style={{ fontSize: "24px" }} />
            </Row>
        </div>

    );
    const genTestContentExtra = () => (
        <div>
            <Row className='mt-3 mr-3'>
                <SendOutlined style={{ fontSize: "24px" }} />
            </Row>
        </div>

    );
    /**
     * Produces final message string by injecting selected parameters
     * into the template text using {{n}} placeholders.
     * Also appends rendered button preview text when present.
     *
     * @returns {string} Renderable message string for API submission
     */
    const sendMessage = () => {
        let regex = /{{(\d+)}}/g;
        let text = campaignState.chooseTemplateMessage;
        let matches = text.match(regex);
        let message = [];
        let bracketop = "{{"
        let bracketcl = "}}"
        let strMessage = "";
        let i = 0;
        if (matches) {
            let currentIndex = 0;

            matches.forEach(matchValue => {

                const number = parseInt(matchValue.match(/\d+/)[0], 10);

                const matchIndex = text.indexOf(matchValue);

                const outsideText = text.slice(currentIndex, matchIndex);
                strMessage += outsideText + bracketop + getCampaignParameter(variable[number - 1]) + bracketcl;

                message.push((<span >{outsideText}</span>));
                message.push((<span className=" text-danger font-size-md">{bracketop}{getCampaignParameter(1)}{bracketcl}</span>));
                currentIndex = matchIndex + matchValue.length;

            });

            // Eğer metin, son eşleşme sonrasında devam ediyorsa, kalan kısmı da ekrana yazdır
            if (currentIndex < text.length) {
                const remainingText = text.slice(currentIndex);
                if (remainingText) {
                    strMessage += remainingText;
                    message.push((<span>{remainingText}</span>));
                }
            }
        } else {
            // Eğer eşleşme bulunamadıysa, metnin tamamını dışındaki metin olarak kabul edebilirsiniz.
            message.push((<span>{text}</span>));
            strMessage += text;
        }
        message.push(changeButtonEdit())
        strMessage += changeButtonEdit();

        return strMessage;

    };
    /**
     * Builds a JSX representation of the message with highlighted variables
     * for UI preview purposes. When `value` is truthy, returns component form;
     * otherwise returns content nodes for lightweight rendering.
     *
     * @param {boolean} value - Toggle for detailed vs. content rendering
     * @returns {JSX.Element[]}
     */
    const messageEdit = (value) => {

        let regex = /{{(\d+)}}/g;
        let text = campaignState.chooseTemplateMessage;
        let matches = text.match(regex);
        let component = [];
        let content = [];
        if (matches) {
            let currentIndex = 0;

            matches.forEach(matchValue => {

                const number = parseInt(matchValue.match(/\d+/)[0], 10);

                const matchIndex = text.indexOf(matchValue);

                const outsideText = text.slice(currentIndex, matchIndex);

                component.push((<span >{outsideText}</span>));
                content.push((<span >{outsideText}</span>));
                // component.push((<span className="py-1 text-danger font-size-lg" >{buttonVariable}</span>));
                // if (variable[number - 1] == null) {
                component.push((<span className=" text-danger  font-size-md">{getCampaignParameterName(variable[number - 1])}</span>));
                content.push((<span className=" m-1 text-danger font-size-md">
                    {(DynamicSelectList(number - 1))}
                </span>));
                // }
                currentIndex = matchIndex + matchValue.length;

            });

            // Eğer metin, son eşleşme sonrasında devam ediyorsa, kalan kısmı da ekrana yazdır
            if (currentIndex < text.length) {
                const remainingText = text.slice(currentIndex);
                component.push((<span>{remainingText}</span>));
                content.push((<span>{remainingText}</span>));
            }
        } else {
            // Eğer eşleşme bulunamadıysa, metnin tamamını dışındaki metin olarak kabul edebilirsiniz.
            component.push((<span>{text}</span>));
            content.push((<span>{text}</span>))

        }

        return value ? component : content;

    };
    /**
     * Filters templates by search input value (case-insensitive).
     *
     * @param {Event} e - Input change event
     */
    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchValue(value);

        setFilteredTemplates(
            templatesData.filter((template) =>
                template.elementName.toLowerCase().includes(value)
            )
        );
    };

    /**
     * Returns stand customers filtered by selected event ids.
     *
     * @returns {Array} Filtered stand customers
     */
    const getStandCustomers = () => {
        const selectedEvent = campaignState.campaignEventCustomers;
        const result = standCustomerList.filter(customer =>
            selectedEvent.indexOf(customer.eventId) != -1
        );

        return result;
    }

    /**
     * Renders customer selection UI based on active group type.
     * Handles event/stand, direct customers, sessions, and split groups.
     *
     * @returns {JSX.Element} Customer selection area
     */
    const customerSelectArea = () => {

        if (selectedCustomerGroupType == customerGroupTypes.eventCustomers) {

            const eventCustomers = getStandCustomers();
            return (
                <Flex flexDirection='column'>
                    <Select
                        mode="multiple"
                        onChange={handleeventCustomerChange}
                        tagRender={tagRender}
                        placeholder={t("selectEvent")}
                        style={{
                            width: '100%',

                        }}
                        filterOption={true}
                        optionFilterProp="label"
                        options={eventCustomerList.map((item) => {
                            return {
                                label: item.eventName + " (" + item.customerCount + ")",
                                value: item.eventId
                            }
                        })}
                    >
                        {/* {eventCustomerList.map((item, index) => (
                            <Option value={item.eventId} label={item.eventName} >
                                {item.eventName + " (" + item.customerCount + ")"}
                            </Option>
                        ))} */}
                    </Select>
                    <Select
                        mode="multiple"
                        onChange={handleStandCustomerChange}
                        tagRender={tagRender}
                        placeholder={t("selectstand")}
                        style={{
                            width: '100%',
                            marginTop: 10
                        }}
                        options={eventCustomers.map((item) => {
                            return {
                                label: item.standName + " (" + item.customerCount + ")",
                                value: item.standId
                            }
                        })}
                    >
                        {/* {eventCustomers.map((item, index) => (
                            <Option value={item.standId} label={item.standName} >
                                {item.standName + " (" + item.customerCount + ")"}
                            </Option>
                        ))} */}
                    </Select>

                </Flex>

            );
        } else if (selectedCustomerGroupType == customerGroupTypes.sessionCustomers) {

            return (
                <div>
                    {isCoffee && activeSessionClients?.length > 0 && (
                        <Select
                            mode="multiple"
                            onChange={handleBranchChange}
                            className='mb-2'
                            tagRender={tagRender}
                            placeholder={t("branch")}
                            style={{
                                width: '100%',
                            }}

                            filterOption={true}
                            optionFilterProp="label"
                            options={branchList.map((item) => {
                                return {
                                    label: item.value,
                                    value: item.key
                                }
                            })}

                        >
                        </Select>
                    )}
                    <Alert message={getCampaignGrupTotalCount() == 0 ? t("noOpenedSessionMessage") :
                        t("sessionMessageInfo").replace("{count}", getCampaignGrupTotalCount())}
                        type={getCampaignGrupTotalCount() == 0 ? "warning" : "info"} >
                    </Alert>
                </div>
            );
        } else if (selectedCustomerGroupType == customerGroupTypes.customerSelect) {

            return (
                <Select
                    mode="multiple"
                    onChange={handleClientSelectChange}
                    tagRender={tagRender}
                    placeholder={t("selectCustomerPlh")}
                    style={{
                        width: '100%',
                    }}
                    filterOption={true}
                    optionFilterProp="label"
                    options={customerList.map((item) => {
                        return {
                            label: item.clientName + " (" + item.clientNumber + ")",
                            value: item.clientId
                        }
                    })}

                >
                </Select>
            );
        } else if (selectedCustomerGroupType == customerGroupTypes.splitedCustomers) {

            return (
                <Select
                    // mode="multiple"
                    onChange={(value, option) => handleChangeSplited(option)}
                    // tagRender={tagRender}
                    style={{
                        width: '100%',
                    }}
                    placeholder={t("groupedCustomersEx")}
                    filterOption={true}
                    optionFilterProp="label"
                    options={splitedCustomerList?.map((customerArray, ind) => ({
                        label: `${t("Group")} ${ind + 1} (${customerArray.length} ${t("customers")})`,
                        value: ind,
                        data: customerArray.map(customer => customer.clientId)
                    }))}

                >
                </Select>
            );
        }
        else {
            return (
                <div className={""}>
                    <Select
                        mode="multiple"
                        onChange={handleToChange}
                        tagRender={tagRender}
                        placeholder={t("selectGroup")}
                        style={{
                            width: '100%',
                        }}
                        filterOption={true}
                        optionFilterProp="label"
                        options={toData.map((item) => {
                            return {
                                label: item.groupname + " (" + item.customercount + ")",
                                value: item.groupid
                            }
                        })}
                    >
                    </Select>
                    {isCoffee && (
                        <Select
                            mode="multiple"
                            onChange={handleBranchChange}
                            className='mt-2'
                            tagRender={tagRender}
                            placeholder={t("branch")}
                            style={{
                                width: '100%',
                            }}

                            filterOption={true}
                            optionFilterProp="label"
                            options={branchList.map((item) => {
                                return {
                                    label: item.value,
                                    value: item.key
                                }
                            })}

                        >
                        </Select>
                    )}
                </div>
            );
        }


    }

    /**
     * Builds radio group options for selecting customer group type.
     * Includes conditional event customers when app context supports it.
     *
     * @returns {Array<{value:string,label:string,style?:Object}>}
     */
    const getGroupSelectRadio = () => {

        let result = [
            { value: customerGroupTypes.customerGroups, label: t('customerGroups') },
            { value: customerGroupTypes.customerSelect, label: t('selectCustomer'), },
            {
                value: customerGroupTypes.sessionCustomers, label: t('sessionCustomerList'),
                style: {
                    marginTop: 15
                }
            },
            { value: customerGroupTypes.splitedCustomers, label: t('specialGroups') },

        ]

        if (isEvent) {
            result.push({ value: customerGroupTypes.eventCustomers, label: t('eventCustomers') });
        }
        return result;
    }



    return (
        <>
            <Spin spinning={loadingPanel} indicator={Icon} >
                <div >
                    {contextHolder}
                    <Row>
                        <Col span={18}>
                            <Card>

                                {CampaignInput()}


                                <Col>
                                    <div>
                                        <Collapse
                                            defaultActiveKey={['2']}
                                            // onChange={onChangeAcco}
                                            expandIconPosition={'start'}
                                            accordion>
                                            <Panel key="1" style={(fromValidation) ? panelErrorStyle : panelInfoStyle} header={headerFromExtra()} extra={genFromExtra()} showArrow={false} >
                                                <Row className='mt-4'>
                                                    <Col span={1}></Col>
                                                    <Col className='' span={7}>
                                                        <div align="left" style={{
                                                            width: "100%",
                                                        }} className=' mt-2' ><h5>{t('senderNumber')}: </h5> </div>
                                                    </Col>
                                                    <Col span={10}>
                                                        <Select
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                            value={campaignState.campaignFrom}
                                                            onChange={
                                                                handleFromChange}>
                                                            {fromData.map((item, index) => (
                                                                <Option key={item.firmbusinessnumberid} value={item.firmbusinessnumberid} >
                                                                    {item.friendlyname + "  (" + item.businessnumber + ")"}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Col>
                                                </Row>
                                            </Panel>
                                            <Panel key="2" style={(toValidation) ? panelErrorStyle : panelInfoStyle} header={headerToExtra()} extra={genToExtra()} showArrow={false} >

                                                <Row className='mt-4'>
                                                    <Col span={1}></Col>
                                                    <Col span={7} className=''>
                                                        <div align="left" className='mt-2'><h5>{t('targetGroups')}: </h5> </div>
                                                    </Col>

                                                    <Col span={10}>
                                                        <div className='mb-4'>
                                                            <Radio.Group defaultValue={1}
                                                                onChange={(event) => setSelectedCustomerGroupType(event.target.value)}
                                                                options={getGroupSelectRadio()}
                                                            >
                                                            </Radio.Group>
                                                        </div>

                                                        {customerSelectArea()}
                                                    </Col>
                                                </Row>

                                            </Panel>
                                            <Panel key="3" style={(chooseTemplateValidation) ? panelErrorStyle : panelInfoStyle} header={headerChooseTempExtra()} extra={genChooseTempExtra()} showArrow={false} >
                                                <div>
                                                    <Row>
                                                        <Col span={9} className=' mt-3 pl-3'>
                                                            <div className="mr-md-3 ">
                                                                <Input placeholder={t("searchTemplate")} onChange={handleSearch} prefix={<SearchOutlined />} />
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <div className="table-responsive  p-3">
                                                        <Table
                                                            pagination={{ pageSize: 6 }}
                                                            columns={columns}
                                                            dataSource={filteredTemplates}
                                                            rowKey='id'
                                                            rowSelection={
                                                                {
                                                                    type: 'radio',
                                                                    columnWidth: 48,
                                                                    onSelect: (record) => {
                                                                        { setVariable("") }

                                                                        try {

                                                                            setCampaignState({
                                                                                ...campaignState,
                                                                                chooseTemplateId: record.id,
                                                                                chooseTemplateMessage: record.headData,
                                                                                chooseTemplateName: record.elementName
                                                                                ,
                                                                                chooseTemplateDynamicCount: record.dynamicCount,
                                                                                chooseTemplateDynamicButtonCount: record.dynamicButtonCount,
                                                                                chooseTemplateButtonsData: record.buttonsData,
                                                                                chooseTemplateType: record.templateType,
                                                                                mediaId: record.mediaId
                                                                            })
                                                                        } catch (error) {
                                                                            alert(error);
                                                                        }


                                                                    }
                                                                }
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </Panel>
                                            <Panel key="4" style={(contentValidation) ? panelErrorStyle : panelInfoStyle} header={headerContentExtra()} extra={genContentExtra()} showArrow={false} >
                                                <div>
                                                    <Row className=' d-flex align-items-center justify-content-center'>
                                                        {/* <Col className='d-flex align-items-center justify-content-center'> <b>Mesaj İçeriği</b> */}
                                                        {/* </Col> */}
                                                        <Col span={22} className='ml-2'>
                                                            <div className=" mt-1 ml-4 mb-4   p-4" style={{ background: "#e8e8e8", borderRadius: 10 }}>
                                                                {/* {component} */}
                                                                {/* {content} */}
                                                                {messageEdit(false)}
                                                            </div>
                                                        </Col>

                                                        <Col span={22} className='ml-2'>

                                                            {contentButton()}
                                                        </Col>
                                                        {/* <Col span={22} className='ml-2'>
                                                    <div className=" ml-4 mb-5 pt-0  p-4" style={{ background: "#e8e8e8", borderRadius: 10 }}>
                                                       
                                                        {changeButton(false)}
                                                    </div>
                                                </Col> */}
                                                    </Row>


                                                </div>
                                            </Panel>
                                            <Panel key="5" style={(contentValidation) ? panelErrorStyle : panelInfoStyle} header={testContentExtra()} extra={genTestContentExtra()} showArrow={false} >
                                                <div>
                                                    <Row gutter={24} align="middle">
                                                        <Col span={12} style={{ textAlign: "left" }}>
                                                            <strong style={{ color: "#555555", fontStyle: "italic", fontSize: "0.9rem" }}>
                                                                {t('phoneNumberInstruction')}
                                                            </strong>
                                                        </Col>
                                                        <Col span={6}>
                                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                                <Input
                                                                    value={testPhone}
                                                                    onChange={testPhoneChange}
                                                                    type="tel"
                                                                    pattern="[0-9][0-9][0-9]{10}"
                                                                    maxLength={12}
                                                                    placeholder={t('enterPhoneNumber')}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col span={6}>

                                                            <ReusableButton
                                                                variant="campaign"
                                                                text={t('sendTestMessage')}
                                                                onClick={sendTestTemplateMessage}
                                                                icon={<SyncOutlined />}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </Panel>
                                        </Collapse>
                                    </div>
                                </Col>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Col style={{ position: "fixed" }} className='pl-2 ml-4 mt-2' span={7}>
                                <div style={{ backgroundColor: "#e5ddd5", alignItems: 'center', justifyContent: 'center' }} className="smartphone">
                                    <br />
                                    <Row>
                                        <Col span={2}></Col>
                                        <Col span={22}>
                                            <div
                                                className="mt-4 mr-2 p-2  content"
                                                style={{
                                                    background: "#d3eebe",
                                                    borderRadius: 10,
                                                    alignItems: 'center', justifyContent: 'center'
                                                }}
                                            >
                                                {templateTypeFor()}
                                                <div className="p-1">{messageEdit(true)}</div>
                                            </div>
                                            <div className="my-1"></div>
                                            <Col>{phoneButton()}</Col>
                                        </Col>
                                    </Row>
                                    <br />
                                </div>


                            </Col>
                        </Col>
                    </Row>
                </div>
            </Spin>
            {
                modalVisible && (
                    <ConfirmationModal
                        visible={modalVisible}
                        onClose={() => setModalVisible(false)}
                        senderNumber={campaignState.campaignFrom}
                        groups={selectedCustomerGroupType == customerGroupTypes.customerGroups ? campaignState.campaignToName :
                            (campaignState.campaignStandCustomersName?.length > 0 ? campaignState.campaignStandCustomersName
                                : campaignState.campaignEventCustomersName)}
                        messageContent={campaignState.chooseTemplateMessage}

                    />)
            }

        </>

    );

}
const styles = {
    table: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        marginTop: '20px',
    },
    cell: {
        padding: '10px',
        textAlign: 'left',
        fontSize: '14px',
        color: '#333',
    },
    cell2: {
        padding: '10px',
        textAlign: 'left',
        fontSize: '14px',
        color: '#333',
    },
    text: {
        fontWeight: '500',
    },
    header: {
        backgroundColor: '#f5f5f5',
        fontWeight: '600',
        color: '#1890ff',
    },
    radioButton: {
        display: 'flex',
        justifyContent: 'center',
    },
    // Row hover style
    rowHover: {
        backgroundColor: '#f0faff',
    },
    selectedRow: {
        backgroundColor: '#e6f7ff',
    },
    // Header styles
    headerTitle: {
        backgroundColor: '#1890ff',
        color: '#fff',
        padding: '12px',
        fontWeight: 'bold',
        fontSize: '16px',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
    },
};


// Export the CreateCampaigns component as default export
export default CreateCampaigns